/**
 * Maltese catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 2.
 *
 * `dir: 'ltr'`. Native-grade Maltese at a mathematician's register. **Warrant →
 * «Ġustifikazzjoni»** (justification — flagged). CLDR cardinal categories are one / two / few /
 * many / other (Maltese takes the plural for few 2–10 but the singular again for many 11–19
 * and other 20+), so `frameActive` declares exactly those (plus an explicit `=0`).
 *
 * NOTE: the few/many noun forms are flagged "expert-review suggested" in `Agent9_REPORT.md`.
 * W/M grades stay Latin (`::numbering-system/latn`). Per INV-STABLE-ID any correction changes
 * only the string and leaves every verdict fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const MT_CATALOG = {
    locale: 'mt',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Inkjesta',
        [P2.sectionClaims]: 'Asserzjonijiet',
        [P2.sectionIntegrity]: 'Integrità',
        [P2.axisWarrantLabel]: 'Ġustifikazzjoni',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empiriku',
        [P2.axisEmpiricalValue]: "{kind, select, defn {Definizzjoni} interp {Interpretazzjoni} testable {Testabbli} tested {Ittestjat} refuted {Konfutat} program {Programm ta' riċerka} philos {Filosofiku} other {Mhux magħruf}}",
        [P2.axisModalLabel]: 'Modali',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: "Livell ta' saħħa {w, number, ::numbering-system/latn}",
        [P2.stateExplorable]: 'Esplorabbli',
        [P2.stateRefuted]: 'Konfutat',
        [P2.stateOutside]: 'Barra mill-kamp',
        [P2.verdictConflict]: "F'kunflitt ma' {constraints}",
        [P2.verdictOutside]: 'Jeħtieġ {missing}',
        [P2.lossRegisterPlain]: 'Issimplifikat għal lingwaġġ komuni — parti mill-preċiżjoni titħalla barra.',
        [P2.lossRegisterKid]: 'Issimplifikat għal qarrejja żgħar — ħafna dettalji jitħallew barra.',
        [P2.lossProfileDetail]: 'Il-profil sħiħ Ġustifikazzjoni / Empiriku / Modali huwa miġbur fil-qosor hawn.',
        [P2.lossConflictDetail]: "Id-dettalji tal-kunflitt huma mqassra f'din il-veduta.",
        [P2.laneVerified]: 'Verifikat',
        [P2.laneUnverified]: 'Mhux verifikat',
        [P2.laneConflict]: 'Kunflitt ippruvat',
        [P2.constraintLogic]: 'Loġika',
        [P2.constraintLightBends]: "Id-dawl jitgħawweġ f'kamp gravitazzjonali",
        [P2.constraintParallelPostulate]: 'Il-postulat tal-paralleli',
        [P2.claimScalarGravity]: 'Teorija skalari tal-gravità',
        [P2.claimNonEuclidean]: 'Ġeometrija mhux Ewklidjana',
        [P2.claimSubstrateSr]: 'Interpretazzjoni tas-sottostrat tar-relatività speċjali',
        [P2.edgeExtObservation]: 'Osservazzjoni esterna',
        [P2.edgeResourceSample]: 'Riżorsa komputazzjonali kondiviża',
        [P2.edgeHeuristicSample]: 'Gwida ewristika',
        [P2.edgeSharedSample]: 'Pedament kondiviż',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Restrizzjonijiet',
        [P2.sectionEdges]: 'Xfar',
        [P2.sectionFrame]: 'Qafas',
        [P2.sectionExposure]: 'Espożizzjoni',
        [P2.statusAdoptedFormal]: 'Adottat (formali)',
        [P2.statusAdoptedInterpretive]: 'Adottat (interpretattiv)',
        [P2.statusSuspended]: 'Sospiż',
        [P2.edgeEffectConstraint]: "Jifforma restrizzjoni (jista' jikkonfuta)",
        [P2.edgeEffectResource]: 'Riżorsa — l-ebda effett fuq il-verdetti',
        [P2.edgeEffectHeuristic]: 'Ewristika — l-ebda effett fuq il-verdetti',
        [P2.edgeEffectShared]: 'Pedament kondiviż — l-ebda effett fuq il-verdetti',
        [P2.frameActive]: '{count, plural, =0 {L-ebda restrizzjoni attiva} one {# restrizzjoni attiva} two {# restrizzjonijiet attivi} few {# restrizzjonijiet attivi} many {# restrizzjoni attiva} other {# restrizzjoni attiva}}',
        [P2.exposureNone]: 'L-ebda dipendenza interpretattiva',
        [P2.exposureDep]: 'Jiddependi minn {id}',
        [P2.laneTrackMachine]: 'Magna',
        [P2.laneTrackExpert]: 'Espert',
        [P2.laneTrackCommunity]: 'Komunità',
        [P2.expertAudited]: 'Awditjat',
        [P2.expertInProgress]: 'Għaddej',
        [P2.expertNotReviewed]: 'Mhux rivedut',
        [P2.communityFlags]: 'Hemm bnadar',
        [P2.communityNone]: 'L-ebda bandiera',
        [P2.controlChallenge]: 'Ikkontesta',
        [P2.controlActivate]: 'Attiva',
        [P2.controlMakeInterpretive]: 'Agħmel interpretattiv',
        [P2.controlImportEdge]: 'Importa xifer',
        [P2.controlPromote]: 'Ippromwovi',
        [P2.controlUndo]: 'Ħassar',
        [P2.controlRedo]: "Erġa' agħmel",
        [P2.controlRegister]: 'Irreġistra',
        [P2.promoteRejected]: 'Promozzjoni miċħuda',
        [P2.promoteAccepted]: 'Promozzjoni aċċettata',
        [P2.promoteReasonRefutationTerminal]: 'Prova ma tistax tfejjaq konfutazzjoni.',
        [P2.promoteReasonKeystone]: 'Promozzjoni ewlenija teħtieġ appoġġ empiriku.',
        [P2.promoteReasonEmpiricalNotFixed]: 'It-tip empiriku mhux iffissat.',
        [P2.promoteReasonModalNotFixed]: 'Il-grad modali mhux iffissat.',
        [P2.promoteReasonWarrantNotImproved]: 'Il-grad ta’ ġustifikazzjoni ma tjiebx.',
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=mt.js.map